import { useEffect, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import {
  sliceKey,
  reducer,
  myautoiqSegmentSettingsActions,
} from 'app/pages/MyautoiqSegmentSettingsComponent/slice';
import { watchMyautoiqSegmentSettingsSaga } from 'app/pages/MyautoiqSegmentSettingsComponent/saga';
import { selectMyautoiqSegmentSettings } from 'app/pages/MyautoiqSegmentSettingsComponent/selectors';
import { DealerSegmentModal } from 'app/pages/DealerSegmentSettingsComponent/components/DealerSegmentModal';
import { DealerSegmentCsvUploadModal } from 'app/pages/DealerSegmentSettingsComponent/components/DealerSegmentCsvUploadModal';
import type { DealerSegmentRow } from 'app/pages/DealerSegmentSettingsComponent/components/DealerSegmentModal';
import { PRIMARY_SEGMENT_ENUM } from 'app/pages/DealerSegmentSettingsComponent/data/constants';

import {
  AccordionComponent,
  AccordionSummaryComponent,
  AccordionDetailsComponent,
} from 'app/components/StyledComponents';
import { SegmentSettingsTable } from 'app/components/ReusableUIComponents/SegmentSettingsTable';

import {
  Typography,
  Box,
  Button,
} from '@mui/material';
import { ConfirmationDialogMAIQ } from 'app/components/ReusableUIComponents/LowLevelComponents/ConfirmationDialogMAIQ';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';

function getSettingsFromResponse(data: any): {
  settingsId: string | null;
  settingsDoc: any;
  segments: DealerSegmentRow[];
} {
  const body = data?.body ?? data?.data ?? data;
  
  // Check if response is direct settings object (myautoiq-settings format)
  if (body && body._id && body.type === 'DEALER_SEGMENTS_CONFIG') {
    const settingsId = body._id;
    const segments = body.properties?.segments ?? [];
    return { settingsId, settingsDoc: body, segments };
  }
  
  // Check if response is array format (dealer-settings format)
  if (body && Array.isArray(body) && body[0]?.settings?.length) {
    const settingsDoc = body[0].settings[0];
    const settingsId = settingsDoc?._id ?? null;
    const segments = settingsDoc?.properties?.segments ?? [];
    return { settingsId, settingsDoc, segments };
  }
  
  return { settingsId: null, settingsDoc: null, segments: [] };
}

/** Group segments by primary_segment; preserve original index for edit/delete. Order by PRIMARY_SEGMENT_ENUM then rest. */
function groupSegmentsByPrimary(
  segments: DealerSegmentRow[],
): { primarySegment: string; items: { row: DealerSegmentRow; index: number }[] }[] {
  const withIndex = (segments || []).map((row, index) => ({ row, index }));
  const byPrimary: Record<string, { row: DealerSegmentRow; index: number }[]> = {};
  withIndex.forEach(({ row, index }) => {
    const key = row.primary_segment || '—';
    if (!byPrimary[key]) byPrimary[key] = [];
    byPrimary[key].push({ row, index });
  });
  const orderedKeys = [
    ...PRIMARY_SEGMENT_ENUM.filter(k => byPrimary[k]?.length),
    ...Object.keys(byPrimary).filter(k => !PRIMARY_SEGMENT_ENUM.includes(k as any)),
  ];
  return orderedKeys.map(primarySegment => ({
    primarySegment,
    items: byPrimary[primarySegment] || [],
  }));
}

interface MyautoiqSegmentSettingsComponentProps {
  manufacturerId: string;
  makeId?: string;
  makeName?: string;
  manufacturerName?: string;
}

export const MyautoiqSegmentSettingsComponent = ({
  manufacturerId,
  makeId,
  makeName,
  manufacturerName,
}: MyautoiqSegmentSettingsComponentProps) => {
  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: watchMyautoiqSegmentSettingsSaga });

  const dispatch = useDispatch();
  const myautoiqSegmentState = useSelector(selectMyautoiqSegmentSettings);
  const { data, loading } = myautoiqSegmentState;

  const { settingsId, settingsDoc, segments } = getSettingsFromResponse(data);

  const [modalOpen, setModalOpen] = useState(false);
  const [editState, setEditState] = useState<{ edit: boolean; index: number }>({
    edit: false,
    index: -1,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number>(-1);
  const [csvUploadOpen, setCsvUploadOpen] = useState(false);

  useEffect(() => {
    if (manufacturerId) {
      dispatch(
        myautoiqSegmentSettingsActions.requestMyautoiqSegmentSettings({
          manufacturerId: manufacturerId,
          makeId,
        }),
      );
    }
  }, [manufacturerId, makeId, dispatch]);

  const updateConfig = useCallback(
    (updatedSegments: DealerSegmentRow[]) => {
      if (!manufacturerId) return;
      if (settingsId && settingsDoc) {
        const payload = {
          ...settingsDoc,
          properties: {
            ...(settingsDoc.properties || {}),
            segments: updatedSegments,
          },
        };
        dispatch(
          myautoiqSegmentSettingsActions.requestUpdateMyautoiqSegmentSettings({
            manufacturerId: manufacturerId,
            makeId,
            settingsId,
            data: payload,
          }),
        );
      } else {
        dispatch(
          myautoiqSegmentSettingsActions.requestCreateMyautoiqSegmentSettings({
            manufacturerId: manufacturerId,
            makeId,
            data: {
              type: 'DEALER_SEGMENTS_CONFIG',
              manufacturer_id: manufacturerId,
              make_id: makeId,
              make_name: makeName,
              manufacturer_name: manufacturerName,
              properties: { segments: updatedSegments },
            },
          }),
        );
      }
    },
    [
      settingsId,
      settingsDoc,
      manufacturerId,
      makeId,
      makeName,
      manufacturerName,
      dispatch,
    ],
  );

  const handleAddConfigurations = () => {
    setEditState({ edit: false, index: -1 });
    setModalOpen(true);
  };

  const handleEdit = (index: number) => {
    setEditState({ edit: true, index });
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditState({ edit: false, index: -1 });
  };

  const handleModalSave = (segment: DealerSegmentRow) => {
    const list = [...segments];
    if (editState.edit && editState.index >= 0) {
      list[editState.index] = segment;
    } else {
      list.push(segment);
    }
    updateConfig(list);
    handleModalClose();
  };

  const handleDeleteClick = (index: number) => {
    setDeleteIndex(index);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (deleteIndex < 0) return;
    const list = segments.filter((_, i) => i !== deleteIndex);
    updateConfig(list);
    setDeleteDialogOpen(false);
    setDeleteIndex(-1);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteIndex(-1);
  };

  const handleCsvApply = useCallback(
    (rows: DealerSegmentRow[]) => {
      updateConfig(rows);
      setCsvUploadOpen(false);
    },
    [updateConfig],
  );

  const hasData = settingsId && settingsDoc;
  const groupedSections = useMemo(
    () => (hasData ? groupSegmentsByPrimary(segments) : []),
    [hasData, segments],
  );

  const segmentOptions = useMemo(() => {
    const primaryFromApi = Array.from(
      new Set(
        (segments || [])
          .map(s => s.primary_segment)
          .filter(Boolean),
      ),
    ) as string[];
    const secondaryFromApi = Array.from(
      new Set(
        (segments || [])
          .map(s => s.secondary_segment)
          .filter(Boolean),
      ),
    ) as string[];
    const tertiaryFromApi = Array.from(
      new Set(
        (segments || [])
          .map(s => s.tertiary_segment)
          .filter(Boolean),
      ),
    ) as string[];
    return {
      primary: primaryFromApi,
      secondary: secondaryFromApi,
      tertiary: tertiaryFromApi,
    };
  }, [segments]);

  const fieldValueByFieldName = useMemo(() => {
    const map: Record<string, string[]> = {};
    const rawSegments = segments || [];
    rawSegments.forEach((s: any) => {
      const fn = (s.field_name ?? s.fieldName ?? '')?.toString().trim();
      const fv = (s.field_value ?? s.fieldValue ?? '')?.toString().trim();
      if (!fn) return;
      if (!map[fn]) map[fn] = [];
      if (fv && !map[fn].includes(fv)) map[fn].push(fv);
    });
    return map;
  }, [segments]);

  return (
    <>
      <AccordionComponent
        elevation={0}
        data-guidance-tour="myautoiq-segment-settings"
        sx={{
          '&::before': { display: 'none' },
          padding: '16px',
          border: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '5px !important',
          marginBottom: '16px',
        }}
      >
        <AccordionSummaryComponent
          expandIcon={<ExpandMoreIcon />}
          aria-controls="myautoiq-segment-settings-content"
          id="myautoiq-segment-settings-header"
        >
          <Typography variant="h6" component="div">
            Segments Settings
          </Typography>
        </AccordionSummaryComponent>
        <AccordionDetailsComponent>
          {loading && (
            <Typography color="textSecondary">Loading...</Typography>
          )}
          {!manufacturerId && (
            <Typography color="textSecondary">
              Select a manufacturer to load myautoiq segment settings.
            </Typography>
          )}

          {!loading && manufacturerId && (
            <>
              <Box sx={{ display: 'flex', gap: 2, marginTop: 2, marginBottom: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddConfigurations}
                >
                  Add Configurations
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => setCsvUploadOpen(true)}
                  disabled={loading}
                >
                  Bulk upload (CSV)
                </Button>
              </Box>
              {!hasData && (
                <Typography color="textSecondary" sx={{ mb: 2 }}>
                  No myautoiq segment config found for this dealer.
                </Typography>
              )}
              {hasData && groupedSections.length > 0 && (
                <SegmentSettingsTable
                  groupedSections={groupedSections}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                  loading={loading}
                  ariaLabelPrefix="myautoiq segments"
                />
              )}
              {hasData && groupedSections.length === 0 && (
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  No segments added yet. Use Add Configurations to add one.
                </Typography>
              )}
            </>
          )}
        </AccordionDetailsComponent>
      </AccordionComponent>

      <DealerSegmentModal
        open={modalOpen}
        onClose={handleModalClose}
        mode={editState.edit ? 'edit' : 'add'}
        initialValues={
          editState.edit && editState.index >= 0
            ? segments[editState.index]
            : null
        }
        onSave={handleModalSave}
        saving={loading}
        segmentOptions={segmentOptions}
        fieldValueByFieldName={fieldValueByFieldName}
      />

      <DealerSegmentCsvUploadModal
        open={csvUploadOpen}
        onClose={() => setCsvUploadOpen(false)}
        onApply={handleCsvApply}
        saving={loading}
      />

      <ConfirmationDialogMAIQ
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Myautoiq Segment config?"
        message="Are you sure you want to delete this segment? This action cannot be undone."
        confirmLabel="Delete"
        confirmColor="primary"
      />
    </>
  );
};
